import { Quiz } from "@garther/interfaces";
import { Box } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";

import { getQuestionMeta, getQuestionText } from "@/libs/utils";

interface QuestionCardProps {
  question: Quiz;
  currentAnswer: any;
  onAnswerChange: (answer: any) => void;
}

// Common option component for single/multiple choice
const OptionItem = ({
  option,
  index,
  isChecked,
  type,
  onChange
}: {
  option: string;
  index: number;
  isChecked: boolean;
  type: "single" | "multiple";
  onChange: (index: number, checked: boolean) => void;
}) => (
  <label
    className={`flex items-center p-4 border rounded-sm cursor-pointer transition-colors ${
      isChecked
        ? "border-background-highlight bg-background-highlight !border-border-dark"
        : "border-border-light hover:border-border-dark"
    }`}
  >
    <input
      type={type === "single" ? "radio" : "checkbox"}
      name={`question-${index}`}
      value={index}
      checked={isChecked}
      onChange={(e) => onChange(index, e.target.checked)}
      className="sr-only"
    />
    <div className="flex items-center space-x-3">
      <div
        className={`w-5 h-5 rounded border e-center ${type === "single" ? "rounded-full" : ""} ${
          isChecked
            ? "border-background-highlight bg-background-highlight !border-border-dark"
            : "border-border-light hover:border-border-dark"
        }`}
      >
        {isChecked &&
          (type === "single" ? (
            <div className="w-3 h-3 bg-background-dark rounded-full"></div>
          ) : (
            <svg className="w-3 h-3 text-background-dark" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          ))}
      </div>
      <span className="body-base text-textcolor-primary">{option}</span>
    </div>
  </label>
);

// Common scale/degree option component
const ScaleOption = ({
  value,
  isSelected,
  onChange,
  size = "w-12 h-12"
}: {
  value: number;
  isSelected: boolean;
  onChange: (value: number) => void;
  size?: string;
}) => (
  <label className="flex flex-col items-center cursor-pointer group">
    <div
      className={`${size} rounded-sm border e-center mb-2 transition-all duration-200 ${
        isSelected
          ? "border-background-highlight bg-background-highlight !border-border-dark"
          : "border-border-light hover:border-border-dark"
      }`}
    >
      <input
        type="radio"
        value={value}
        checked={isSelected}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="sr-only"
      />
      <span className="text-responsive-lg font-normal">{value}</span>
    </div>
  </label>
);

export default function QuestionCard({ question, currentAnswer, onAnswerChange }: QuestionCardProps) {
  const { t } = useI18n();
  const meta = getQuestionMeta(question);

  const renderSingleChoice = () => (
    <div className="space-y-3">
      {meta.options?.map((option: string, index: number) => (
        <OptionItem
          key={index}
          option={option}
          index={index}
          isChecked={currentAnswer === index}
          type="single"
          onChange={(index) => onAnswerChange(index)}
        />
      ))}
    </div>
  );

  const renderMultipleChoice = () => (
    <div className="space-y-3">
      {meta.options?.map((option: string, index: number) => {
        const isChecked = Array.isArray(currentAnswer) && currentAnswer.includes(index);
        return (
          <OptionItem
            key={index}
            option={option}
            index={index}
            isChecked={isChecked}
            type="multiple"
            onChange={(index, checked) => {
              const currentArray = Array.isArray(currentAnswer) ? currentAnswer : [];
              if (checked) {
                onAnswerChange([...currentArray, index]);
              } else {
                onAnswerChange(currentArray.filter((item) => item !== index));
              }
            }}
          />
        );
      })}
    </div>
  );

  const renderScale = () => {
    if (!meta.scaleRange) return null;
    const { min, max, labels } = meta.scaleRange;

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-4">
          <span className="body-base text-textcolor-secondary font-normal">{labels[0]}</span>
          <span className="body-base text-textcolor-secondary font-normal">{labels[1]}</span>
        </div>
        <div className="flex justify-between items-center space-x-3">
          {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((value) => (
            <ScaleOption
              key={value}
              value={value}
              isSelected={currentAnswer === value}
              onChange={onAnswerChange}
            />
          ))}
        </div>
        <div className="text-center pt-4">
          <span className="body-base text-textcolor-secondary font-normal">
            {currentAnswer !== undefined && currentAnswer !== null
              ? t("quiz.page.selectedLevel", { level: currentAnswer, max: max })
              : t("quiz.page.selectRating")}
          </span>
        </div>
      </div>
    );
  };

  const renderDegree = () => {
    if (!meta.degreeRange) return null;
    const { min, max, labels } = meta.degreeRange;

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-4">
          <span className="body-base text-textcolor-secondary font-normal">{labels[0]}</span>
          <span className="body-base text-textcolor-secondary font-normal">{labels[1]}</span>
        </div>
        <div className="flex justify-between items-center space-x-2">
          {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((value) => (
            <ScaleOption
              key={value}
              value={value}
              isSelected={currentAnswer === value}
              onChange={onAnswerChange}
              size="w-10 h-10"
            />
          ))}
        </div>
        <div className="text-center pt-4">
          <span className="body-base text-textcolor-secondary font-normal">
            {currentAnswer !== undefined && currentAnswer !== null
              ? t("quiz.page.selectedLevel", { level: currentAnswer, max: 10 })
              : t("quiz.page.selectAgreement")}
          </span>
        </div>
      </div>
    );
  };

  const renderQuestionOptions = () => {
    switch (question.type) {
      case "single":
        return renderSingleChoice();
      case "multiple":
        return renderMultipleChoice();
      case "scale":
        return renderScale();
      case "degree":
        return renderDegree();
      default:
        return null;
    }
  };

  return (
    <Box>
      <div className="space-y-8">
        <div>
          <h2 className="text-textcolor-primary text-responsive-2xl mb-6 leading-tight">
            {getQuestionText(question)}
          </h2>
          {renderQuestionOptions()}
        </div>
      </div>
    </Box>
  );
}
